import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { Box, ButtonV2, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useState } from 'react';
import { ContentContainer } from './SeoContentSection.styles';

// Render section titles as H2 for SEO — the save-the-date page previously had
// zero H2 tags, hurting the "save the date" keyword cluster (GROW-1788).
const Title: React.FC = ({ children }) => {
  return (
    <TextV2
      fontWeight={600}
      as="h2"
    >
      {children}
    </TextV2>
  );
};

const Paragraph: React.FC = ({ children }) => {
  return <TextV2>{children}</TextV2>;
};

const SeoContentList: React.FC = ({ children }) => {
  const { tq } = useTranslation('stationery');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      width="100%"
      maxWidth={pxToRem(1248)}
      paddingX={6}
      paddingY={10}
      marginX="auto"
      display="grid"
      gap={6}
    >
      <ContentContainer
        position="relative"
        maxHeight={isOpen ? 'auto' : pxToRem(400)}
        overflow={isOpen ? 'auto' : 'hidden'}
      >
        {children}
        {!isOpen && (
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height={pxToRem(100)}
            background="linear-gradient(to bottom, rgba(255, 255, 255, 0), white)"
          />
        )}
      </ContentContainer>
      <ButtonV2
        variant="link"
        intent="neutral"
        onClick={() => setIsOpen(p => !p)}
        typographyVariant="body2"
        fontWeight={600}
        justifyContent="flex-start"
      >
        {isOpen ? tq`Read Less` : tq`Read More`}
      </ButtonV2>
    </Box>
  );
};

type SeoContentSectionProps = {
  category: StationeryTemplateCategoryEnum;
};

export const SeoContentSection: React.FC<SeoContentSectionProps> = ({ category }) => {
  const { tq } = useTranslation('stationery');

  if (category === StationeryTemplateCategoryEnum.saveTheDate) {
    return (
      <SeoContentList>
        <Title>{tq`Announce Your Wedding Date with a Save the Date Card`}</Title>
        <Paragraph>{tq`Whether you mail Save the Dates or send them digitally, we'll ensure a seamless stationery experience`}</Paragraph>
        <Paragraph>{tq`A Save the Date card does more than just claim your wedding date. It frames a momentous occasion, announces an exciting first step towards the aisle, and should set the tone for your big day to come. Reflecting your personality and offering a glimpse of how your celebration will look and feel.`}</Paragraph>
        <Paragraph>{tq`We believe Save the Date cards are so much more than a formality. Which is why we've created a way for you to design yours easily, and quickly online. Whether you choose to craft from scratch, or tailor one of our expertly designed templates. Joy will help you build the anticipation for your wedding, and reflect your unique love story.`}</Paragraph>

        <Title>{tq`The Benefits of Save the Dates`}</Title>
        <Paragraph>{tq`Undecided on Save the Dates at this stage? We're not here to convince you, but we'd love to share a few benefits of sending out Save the Dates that we think you'll find useful.`}</Paragraph>

        <Title>{tq`Why Save the Dates Help Couples Save Money`}</Title>
        <Paragraph>{tq`Weddings are costly affairs, and unfortunately, not every desire can be fulfilled. But there's no need to skip on things like Save the Dates when they can be created and sent for free online!`}</Paragraph>

        <Title>{tq`How Save the Dates Improve Wedding Planning`}</Title>
        <Paragraph>{tq`Creating a realistic budget as early in the wedding planning process as possible is key to not overspending. Save the Dates play a pivotal role in gathering information, helping you to quickly understand the guest count you're working with.`}</Paragraph>
        <Paragraph>{tq`Our connected online RSVP allows you to effortlessly gather details from guests, whether it's an attendance yes/no or something more specific. All of which is then automatically stored in your private online guest list. An invaluable resource at every stage of the wedding planning journey.`}</Paragraph>

        <Title>{tq`Why Sending Save the Dates Increases Guest Attendance`}</Title>
        <Paragraph>{tq`This point is often easily overlooked, but if your wedding falls during a popular month for celebrations or vacations, you need guests to mark your date on their calendars ASAP. To avoid any clashes or disappointing no shows.`}</Paragraph>
        <Paragraph>{tq`Guest availability is even more critical if you're planning a destination wedding, as guests will need more time to prepare for the travel and cost of attending your celebration.`}</Paragraph>

        <Title>{tq`How Save the Dates Introduce Your Wedding Style`}</Title>
        <Paragraph>{tq`Our couples like to think of Save the Dates as previews of their overall wedding theme. A little taster of what's to come on the big day, and a cohesive element in their wedding stationery suite.`}</Paragraph>
        <Paragraph>{tq`When you choose a Joy save-the-date template, we don't just offer the card design—we gift you the entire package. You'll receive a wedding website, save-the-date, invitation, and printed materials, all designed with one coordinated theme. This ensures every detail of your wedding feels polished and cohesive, with a strong, consistent theme. Everything is crafted using high-quality designs to make your wedding experience even more memorable.`}</Paragraph>

        <Title>{tq`Customize and Personalize Your Save the Date Cards`}</Title>
        <Paragraph>{tq`Joy makes it so easy to curate a wedding suite, complete with a stunning Save the Date that reflects your love story.`}</Paragraph>

        <Title>{tq`Start with Personality`}</Title>
        <Paragraph>{tq`We make the art of Save the Date designing effortless and fun, by giving you the power to personalize as you wish. In just a couple of clicks you can transform one of our chic templates, or a blank page, into a unique Save the Date card that truly reflects your special day.`}</Paragraph>
        <Paragraph>{tq`You are free to experiment with fonts, colors, and the design of our templates to build something that feels yours. While also excitedly hinting at the overall wedding theme.`}</Paragraph>

        <Title>{tq`Share Your Engagement Photos`}</Title>
        <Paragraph>{tq`What better way to share your wonderful engagement photos than on your Save the Dates! You'll of course be featuring them on your wedding website, and perhaps on the big day itself. But Save the Dates offer the best opportunity to showcase your favorite image from the shoot, and really celebrate how amazing you guys look together!`}</Paragraph>
        <Paragraph>{tq`You are free to upload your engagement photography to our Save the Date builder, and help give any of our templates a more unique, personal touch. Whether it's a candid snap taken in the moment, or something more professional. Placing your chosen picture within your Save the Dates will help to make the announcement feel even more significant.`}</Paragraph>

        <Title>{tq`Why Choose Save-the-Date Cards for Your Wedding`}</Title>
        <Paragraph>{tq`WithJoy gives you the ability to create Save the Dates at your convenience, and then track them every step of the way.`}</Paragraph>

        <Title>{tq`Seamless Save the Date Integration`}</Title>
        <Paragraph>{tq`Our Save the Date templates are not standalone pieces, they are designed as part of a wedding stationery suite. Meaning your wedding website, invitation (digital and physical) and your Save the Date card will all be coordinated.`}</Paragraph>
        <Paragraph>{tq`This coordination goes beyond aesthetics, as our Save the Date cards and invitations can be integrated within your wedding website. Giving you the ability to request RSVPs online, and additional details from your guests before even sending out the wedding invitations. This is incredibly useful if you don't have addresses for all of your guests, or require information that'll directly affect your wedding plans.`}</Paragraph>
        <Paragraph>{tq`Having your Save the Date linked to your wedding website also allows you to share information about your wedding day along the way. Ensuring guests are always up-to-date with your event as it evolves.`}</Paragraph>

        <Title>{tq`Free Wedding Planning Tools`}</Title>
        <Paragraph>{tq`We're committed to helping couples plan their best day, and we know how quickly wedding costs can add up. That's why Joy offers free wedding websites, planning tools, guest list management, and digital save the dates—so you can simplify planning without added expense. And when you're ready to elevate the experience, you can explore our premium digital invitations and printed stationery, designed to make a lasting impression.`}</Paragraph>

        <Title>{tq`Announce Your Wedding Date Today`}</Title>
        <Paragraph>{tq`Explore how Joy can help you create the perfect save-the-date card—and so much more—by browsing our free templates today. Whether you're drawn to minimalistic designs, like our modern minimalist save-the-date card, or something with a more intricate touch, you're sure to find a style that suits your theme and reflects your shared personality. And if not, we've made it easy to start with a blank page and turn it into something uniquely yours.`}</Paragraph>
        <Paragraph>{tq`So whether you're selecting foil color, letterpress, or a matte finish monogram, you can create beautiful save-the-dates that effortlessly match your wedding day. After all, save-the-dates are not just a formality; they are the beginning of your story. Start telling yours with Joy today.`}</Paragraph>
      </SeoContentList>
    );
  }

  return null;
};
